import React from 'react';
import Card from '../../components/ui/Card.jsx';
import Input from '../../components/ui/Input.jsx';
import Button from '../../components/ui/Button.jsx';
import { apiFetch } from '../../api.js';
import { useApi } from '../../hooks/useApi.js';
import { RouteContext } from '../../router.jsx';

const semesterOptions = [
  { value: '', label: 'Select semester' },
  { value: 'Fall', label: 'Fall' },
  { value: 'Spring', label: 'Spring' },
  { value: 'Summer', label: 'Summer' }
];
const campusDomain = 'calstatela.edu';

export default function StaffStudentNew() {
  const { navigate } = React.useContext(RouteContext);
  const { data: me, loading: meLoading, error: meError } = useApi('/api/me');
  const canAddStudent = !!me?.user && (
    me.user.admin_privilege === 1
    || me.user.chair_privilege === 1
    || me.user.edit_privilege === 1
  );
  const [form, setForm] = React.useState({
    cin: '',
    firstName: '',
    lastName: '',
    username: '',
    personalEmail: '',
    startSemester: '',
    startYear: ''
  });
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState({ text: '', tone: 'info' });

  const updateField = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const updateDigitsField = (key, maxLength) => (event) => {
    const digits = event.target.value.replace(/\D/g, '').slice(0, maxLength);
    setForm((prev) => ({ ...prev, [key]: digits }));
  };

  const validate = () => {
    const cin = form.cin.trim();
    if (!cin) return 'CIN is required.';
    if (!/^\d{9}$/.test(cin)) return 'CIN must be exactly 9 digits.';

    const campusEmail = form.username.trim();
    if (!campusEmail) return 'Campus username is required.';
    const campusPattern = new RegExp(`^[^@\\s]+@${campusDomain.replace('.', '\\.')}$`, 'i');
    if (!campusPattern.test(campusEmail)) {
      return `Campus username must end with @${campusDomain}.`;
    }

    if (!form.personalEmail.trim()) return 'Personal email is required.';

    const yearText = form.startYear.trim();
    if (!yearText) return 'Start year is required.';
    if (!/^\d{4}$/.test(yearText)) return 'Start year must be a 4-digit year.';
    const yearValue = Number.parseInt(yearText, 10);
    if (yearValue < 2020 || yearValue > 2050) {
      return 'Start year must be between 2020 and 2050.';
    }
    return '';
  };

  const handleSubmit = async () => {
    if (!canAddStudent) {
      setMessage({ text: 'You do not have access to add students.', tone: 'error' });
      return;
    }
    const validation = validate();
    if (validation) {
      setMessage({ text: validation, tone: 'error' });
      return;
    }
    setSaving(true);
    setMessage({ text: '', tone: 'info' });
    try {
      const campusEmail = form.username.trim().toLowerCase();
      const startYear = Number.parseInt(form.startYear, 10);
      const payload = {
        cin: Number(form.cin),
        username: campusEmail,
        email: campusEmail,
        personal_email: form.personalEmail.trim(),
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        start_semester: form.startSemester.trim(),
        start_year: Number.isFinite(startYear) ? startYear : 0
      };
      const result = await apiFetch('/api/students', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      navigate(`/staff/students/${result?.id}`);
    } catch (err) {
      const messageByCode = {
        student_cin_exists: 'This CIN is already in use.',
        student_username_exists: 'This campus username is already in use.'
      };
      setMessage({
        text: messageByCode[err?.code] || err?.message || 'Failed to create student.',
        tone: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>New Student</h1>
          <p className="subtle">Add a student record and seed their study plan.</p>
        </div>
        <Button variant="secondary" type="button" onClick={() => navigate('/staff/students')}>Back to Students</Button>
      </div>

      {meError ? <div className="alert error">{meError}</div> : null}
      {message.text ? <div className={message.tone === 'error' ? 'alert error' : 'alert'}>{message.text}</div> : null}

      {meLoading ? (
        <p className="subtle">Loading...</p>
      ) : !canAddStudent ? (
        <div className="alert error">You do not have access to add students.</div>
      ) : (
        <Card title="Student Profile">
        <div className="form-grid">
          <Input
            label="CIN"
            value={form.cin}
            onChange={updateDigitsField('cin', 9)}
            placeholder="123456789"
            inputMode="numeric"
            maxLength={9}
            helper="9 digits, numbers only."
          />
          <Input label="First Name" value={form.firstName} onChange={updateField('firstName')} />
          <Input label="Last Name" value={form.lastName} onChange={updateField('lastName')} />
          <Input
            label="Campus Username"
            value={form.username}
            onChange={updateField('username')}
            placeholder={`student@${campusDomain}`}
            helper={`Must end with @${campusDomain}.`}
            type="email"
          />
          <Input label="Personal Email" value={form.personalEmail} onChange={updateField('personalEmail')} placeholder="student@example.com" />
          <label className="input-field">
            <span className="input-label">Start Semester</span>
            <select className="input" value={form.startSemester} onChange={updateField('startSemester')}>
              {semesterOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <Input
            label="Start Year"
            value={form.startYear}
            onChange={updateDigitsField('startYear', 4)}
            placeholder="2024"
            inputMode="numeric"
            maxLength={4}
            helper="Four digits between 2020 and 2050."
          />
        </div>
        <div className="form-actions">
          <Button variant="secondary" type="button" onClick={() => navigate('/staff/students')}>Cancel</Button>
          <Button type="button" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : 'Create Student'}</Button>
        </div>
        </Card>
      )}
    </div>
  );
}
